import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import { conf } from '../confg';
import './NetworkIssueModal.css';

const PING_TIMEOUT_MS = 8000;
const SPEED_TIMEOUT_MS = 12000;
const RECHECK_MS = 15000;
const IDLE_SPEED_CHECK_MS = 60000;

/** اگر پینگ از این بیشتر باشد، کند حساب می‌شود */
const SLOW_RTT_MS = 2500;
/** اگر سرعت دانلود کمتر از این باشد (Mbps)، کند حساب می‌شود */
const SLOW_MBPS = 0.35;
/** حجم نمونه برای سنجش سرعت (بایت) */
const SPEED_PROBE_BYTES = 40960;

function readBrowserConnectionHint() {
  const conn =
    typeof navigator !== 'undefined'
      ? navigator.connection || navigator.mozConnection || navigator.webkitConnection
      : null;
  if (!conn) return null;
  const type = String(conn.effectiveType || '').toLowerCase();
  const downlink = Number(conn.downlink);
  return {
    effectiveType: type,
    downlink: Number.isFinite(downlink) ? downlink : null,
    saveData: Boolean(conn.saveData),
    isSlowHint:
      type === '2g' ||
      type === 'slow-2g' ||
      (Number.isFinite(downlink) && downlink > 0 && downlink < 0.5),
  };
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('timeout')), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

/** پروب health با fetch ساده — بدون هدر سفارشی و بدون interceptor اکسیوس */
async function probeConnection() {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return {
      ok: false,
      kind: 'offline',
      rttMs: null,
      mbps: null,
      browser: readBrowserConnectionHint(),
    };
  }

  const browser = readBrowserConnectionHint();
  let rttMs = null;
  let mbps = null;
  const healthBase = `${conf.server_url}health`;

  // 1) پینگ سبک
  const pingStart = performance.now();
  try {
    const res = await withTimeout(
      fetch(`${healthBase}?_t=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
      }),
      PING_TIMEOUT_MS
    );
    rttMs = Math.round(performance.now() - pingStart);
    if (!res || res.status <= 0) {
      return { ok: false, kind: 'offline', rttMs, mbps, browser };
    }
  } catch (e) {
    rttMs = Math.round(performance.now() - pingStart);
    // اگر مرورگر آنلاین است ولی health به هر دلیل (CORS و غیره) fail شد، مودال قطع نیاور
    if (typeof navigator !== 'undefined' && navigator.onLine) {
      return { ok: true, kind: 'ok', rttMs, mbps, browser, probeIgnored: true };
    }
    return { ok: false, kind: 'offline', rttMs, mbps, browser };
  }

  // 2) سنجش سرعت با دانلود نمونه کوچک (عدد به کاربر نشان داده نمی‌شود)
  const speedStart = performance.now();
  try {
    const speedRes = await withTimeout(
      fetch(`${healthBase}?bytes=${SPEED_PROBE_BYTES}&_t=${Date.now()}`, {
        method: 'GET',
        cache: 'no-store',
        credentials: 'omit',
      }),
      SPEED_TIMEOUT_MS
    );
    const buf = await speedRes.arrayBuffer();
    const elapsedSec = Math.max(0.001, (performance.now() - speedStart) / 1000);
    const sizeBytes = (buf && buf.byteLength) || SPEED_PROBE_BYTES;
    mbps = (sizeBytes * 8) / (elapsedSec * 1000 * 1000);
  } catch (e) {
    mbps = null;
  }

  const slowByRtt = rttMs != null && rttMs >= SLOW_RTT_MS;
  const slowByMbps = mbps != null && mbps < SLOW_MBPS;
  const slowByBrowser = Boolean(browser && browser.isSlowHint);

  if (slowByRtt || slowByMbps || slowByBrowser) {
    return { ok: false, kind: 'slow', rttMs, mbps, browser };
  }

  return { ok: true, kind: 'ok', rttMs, mbps, browser };
}

/**
 * مودال هشدار قطعی / کندی اینترنت برای مشتری
 */
const NetworkIssueModal = () => {
  const [open, setOpen] = useState(false);
  const [checking, setChecking] = useState(false);
  const [kind, setKind] = useState('offline'); // offline | slow
  const dismissedRef = useRef(false);
  const openRef = useRef(false);
  const lastKindRef = useRef('offline');

  const showModal = useCallback((nextKind = 'offline') => {
    if (dismissedRef.current && openRef.current && lastKindRef.current === nextKind) {
      return;
    }
    lastKindRef.current = nextKind;
    openRef.current = true;
    setKind(nextKind);
    setOpen(true);
  }, []);

  const hideModal = useCallback(() => {
    dismissedRef.current = true;
    openRef.current = false;
    setOpen(false);
  }, []);

  const markOnline = useCallback(() => {
    dismissedRef.current = false;
    openRef.current = false;
    setOpen(false);
  }, []);

  const runCheck = useCallback(async () => {
    setChecking(true);
    const result = await probeConnection();
    setChecking(false);

    if (result.ok) {
      markOnline();
      return true;
    }

    dismissedRef.current = false;
    showModal(result.kind === 'slow' ? 'slow' : 'offline');
    return false;
  }, [markOnline, showModal]);

  useEffect(() => {
    const onOffline = () => {
      dismissedRef.current = false;
      showModal('offline');
    };
    const onOnline = () => {
      runCheck();
    };

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);

    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      showModal('offline');
    } else {
      runCheck();
    }

    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const url = String((error.config && error.config.url) || '');
        // خطای خود health را دوباره مودال نکن (لوپ / CORS کاذب)
        if (url.indexOf('/health') !== -1) {
          return Promise.reject(error);
        }

        const noResponse = !error.response;
        const isCancel = axios.isCancel && axios.isCancel(error);
        const code = error.code || '';
        const message = String(error.message || '');
        const networkFail =
          noResponse &&
          !isCancel &&
          typeof navigator !== 'undefined' &&
          navigator.onLine === false &&
          (code === 'ERR_NETWORK' ||
            code === 'ECONNABORTED' ||
            message.toLowerCase().includes('network') ||
            message.toLowerCase().includes('timeout'));

        if (networkFail) {
          dismissedRef.current = false;
          showModal(code === 'ECONNABORTED' ? 'slow' : 'offline');
        }
        return Promise.reject(error);
      }
    );

    const intervalId = setInterval(() => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        if (!dismissedRef.current) showModal('offline');
        return;
      }
      if (openRef.current) {
        runCheck();
      }
    }, RECHECK_MS);

    const idleSpeedId = setInterval(() => {
      if (openRef.current) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      runCheck();
    }, IDLE_SPEED_CHECK_MS);

    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      axios.interceptors.response.eject(interceptorId);
      clearInterval(intervalId);
      clearInterval(idleSpeedId);
    };
  }, [runCheck, showModal]);

  const isSlow = kind === 'slow';
  const title = isSlow ? 'اتصال اینترنت شما ضعیف است' : 'اختلال در اتصال اینترنت';
  const text = isSlow
    ? 'به‌نظر می‌رسد سرعت یا کیفیت اینترنت شما پایین است و ممکن است سایت کند شود یا قطع و وصل کار کند. لطفاً Wi-Fi یا داده موبایل را بررسی کنید و در صورت روشن بودن VPN آن را خاموش نمایید.'
    : 'ارتباط شما با اینترنت یا سرور برقرار نیست. لطفاً اتصال اینترنت گوشی/مودم را بررسی کنید، VPN را خاموش کنید و دوباره تلاش نمایید.';

  return (
    <Modal
      show={open}
      onHide={hideModal}
      backdrop='static'
      keyboard={false}
      centered
      className={`network-issue-modal ${isSlow ? 'is-slow' : 'is-offline'}`}
      style={{ zIndex: 10000000001 }}
    >
      <Modal.Body className='network-issue-modal__body'>
        <div className='network-issue-modal__icon' aria-hidden>
          !
        </div>
        <h3 className='network-issue-modal__title'>{title}</h3>
        <p className='network-issue-modal__text'>{text}</p>

        <div className='network-issue-modal__actions'>
          <Button
            className='network-issue-modal__btn network-issue-modal__btn--primary'
            disabled={checking}
            onClick={runCheck}
          >
            {checking ? 'در حال بررسی...' : 'تلاش مجدد'}
          </Button>
          <Button className='network-issue-modal__btn network-issue-modal__btn--ghost' onClick={hideModal}>
            متوجه شدم
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default NetworkIssueModal;
